import {
  FormController,
  FormFileUpload,
  FormInput,
  FormTextArea,
} from '@/components/form';
import { CoreApi, ProcurementApi } from '@/state/api';
import { CoreType, ProcurementType } from '@/types';
import { getFileNameWithoutExtension } from '@/utils/utilFunctions';
import {
  Box,
  Card,
  FileUploadFileMimeType,
  Flex,
  Stack,
} from '@chakra-ui/react';
import { skipToken } from '@reduxjs/toolkit/query';
import { OverlayDialog, toasterApi } from '@ui';
import dayjs from 'dayjs';
import { Control, FieldErrors, UseFormRegister } from 'react-hook-form';
import z from 'zod';

const Inner_InvoicesManageCard = ({
  isNew,
  po_id,
  invoice_id,
  onClose,
}: InvoicesManageCardProps) => {
  const baseFormSchema =
    ProcurementType.PurchaseOrders.postPurchaseOrderInvoice.body
      .omit({
        file: true,
        filename: true,
      })
      .extend({
        attachmentFile: z.instanceof(File, {
          error: 'Attachment file is required',
        }),
      });
  const formSchema = isNew ? baseFormSchema : baseFormSchema?.partial();
  type FormData = z.infer<typeof formSchema>;

  //  Create purchase order invoice mutation
  const [postPurchaseOrderInvoiceMutation] =
    ProcurementApi.Api.usePostPurchaseOrderInvoiceMutation();

  //  Update purchase order invoice mutation
  const [patchPurchaseOrderInvoiceMutation] =
    ProcurementApi.Api.usePatchPurchaseOrderInvoiceMutation();

  //  Get attachments presigned urls
  const [postAttachmentsPresignedUrlsMutation] =
    CoreApi.Api.usePostAttachmentsPresignedUrlsMutation();

  // Get current purchase order invoice
  const { data: currentPurchaseOrderInvoiceQuery } =
    ProcurementApi.Api.useGetPurchaseOrderInvoiceQuery(
      po_id && invoice_id && !isNew
        ? {
            params: {
              po_id,
              invoice_id,
            },
          }
        : skipToken,
    );

  const onFormSubmit = (formData: FormData) => {
    OverlayDialog.open('confirmPurchaseOrderInvoiceUpdate', {
      overlayId: 'confirmPurchaseOrderInvoiceUpdate',
      title: `Confirm ${isNew ? 'Purchase Order Invoice Creation' : `Purchase Order Invoice Update`}`,
      description: `Are you sure you want to ${
        isNew ? 'create' : 'update'
      } the purchase order invoice?`,

      handleConfirm: async () => {
        type PayloadType =
          | ProcurementType.PurchaseOrders.PostPurchaseOrderInvoice['body']
          | ProcurementType.PurchaseOrders.PatchPurchaseOrderInvoice['body'];

        let uploadedFile: { file: string; filename: string } | undefined;
        if (formData?.attachmentFile) {
          // Upload attachments to S3
          const getPresignedUrlsPayload: CoreType.PostAttachmentsPresignedUrls['body'] =
            [
              {
                module: 'invoice',
                filename:
                  getFileNameWithoutExtension(formData?.attachmentFile?.name) ??
                  '',
                content_type: formData?.attachmentFile?.type ?? '',
                size: formData?.attachmentFile?.size,
              },
            ];

          const getPresignedUrls = await postAttachmentsPresignedUrlsMutation({
            body: getPresignedUrlsPayload,
          });

          const uploadResponse = getPresignedUrls?.data?.map(async (item) => {
            const file = formData?.attachmentFile;
            if (!item || !file) return;

            await fetch(item.upload_url, {
              method: 'PUT',
              headers: { 'Content-Type': file.type },
              body: file,
            });

            return {
              filename: item.filename,
              file: item.file_url,
            };
          });

          const uploadResults = await Promise.allSettled(uploadResponse ?? []);
          const successfullyUploadedFiles = uploadResults
            ?.filter((r) => r.status === 'fulfilled')
            .map((r) => r.value);

          uploadedFile = successfullyUploadedFiles?.[0];
        }

        const payload: PayloadType = {
          ...(formData?.due_date && {
            due_date: formData?.due_date,
          }),
          ...(formData?.freight_amount && {
            freight_amount: formData?.freight_amount,
          }),
          ...(formData?.invoice_date && {
            invoice_date: formData?.invoice_date,
          }),
          ...(formData?.invoice_number && {
            invoice_number: formData?.invoice_number,
          }),
          ...(formData?.notes && {
            notes: formData?.notes,
          }),
          ...(formData?.subtotal_amount && {
            subtotal_amount: formData?.subtotal_amount,
          }),
          ...(formData?.tax_amount && {
            tax_amount: formData?.tax_amount,
          }),
          ...(formData?.total_amount && {
            total_amount: formData?.total_amount,
          }),
          ...(uploadedFile && {
            file: uploadedFile?.file,
            filename: uploadedFile?.filename,
          }),
        };

        const responseData = isNew
          ? await postPurchaseOrderInvoiceMutation({
              params: {
                po_id: po_id!,
              },
              body: payload as ProcurementType.PurchaseOrders.PostPurchaseOrderInvoice['body'],
            })
          : await patchPurchaseOrderInvoiceMutation({
              params: {
                po_id: po_id!,
                invoice_id: invoice_id!,
              },
              body: payload,
            });

        if (responseData) {
          toasterApi({
            type: 'success',
            status: isNew ? 201 : 200,
            code: 'Success',
            message: `${isNew ? 'Created' : 'Updated'} Purchase Order Invoice`,
          });
        }

        // Trigger onclose callback if provided to close the dialog
        onClose?.();
      },
    });
  };

  return (
    <Card.Root width="100%" variant="elevated">
      <Card.Body gap="7">
        <FormController
          key={`purchase-order-invoice-form-${isNew}`}
          label={
            isNew
              ? 'Create Purchase Order Invoice'
              : 'Update Purchase Order Invoice Details'
          }
          formSchema={formSchema}
          helperText={`Please fill out the form to ${
            isNew
              ? 'create a new purchase order invoice'
              : 'update the purchase order invoice details'
          }.`}
          onFormSubmit={onFormSubmit}
          defaultValues={
            isNew
              ? {
                  due_date: '',
                  invoice_date: '',
                  invoice_number: '',
                  freight_amount: '',
                  subtotal_amount: '',
                  tax_amount: '',
                  total_amount: '',
                  notes: '',
                }
              : {
                  due_date:
                    currentPurchaseOrderInvoiceQuery?.due_date &&
                    dayjs(currentPurchaseOrderInvoiceQuery?.due_date).isValid()
                      ? dayjs(
                          currentPurchaseOrderInvoiceQuery?.due_date,
                        ).format('YYYY-MM-DD')
                      : '',
                  invoice_date:
                    currentPurchaseOrderInvoiceQuery?.invoice_date &&
                    dayjs(
                      currentPurchaseOrderInvoiceQuery?.invoice_date,
                    ).isValid()
                      ? dayjs(
                          currentPurchaseOrderInvoiceQuery?.invoice_date,
                        ).format('YYYY-MM-DD')
                      : '',
                  invoice_number:
                    currentPurchaseOrderInvoiceQuery?.invoice_number ?? '',
                  freight_amount:
                    currentPurchaseOrderInvoiceQuery?.freight_amount ?? '',
                  subtotal_amount:
                    currentPurchaseOrderInvoiceQuery?.subtotal_amount ?? '',
                  tax_amount:
                    currentPurchaseOrderInvoiceQuery?.tax_amount ?? '',
                  total_amount:
                    currentPurchaseOrderInvoiceQuery?.total_amount ?? '',
                  notes: currentPurchaseOrderInvoiceQuery?.notes ?? '',
                }
          }
        >
          {({
            register,
            control,
            errors,
          }: {
            register: UseFormRegister<FormData>;
            control: Control<FormData>;
            errors: FieldErrors<FormData>;
          }) => {
            return (
              <Flex flexDirection="column" gapY={4}>
                <Flex gapX={4}>
                  <FormInput
                    register={register}
                    control={control}
                    errors={errors}
                    label="Invoice Date"
                    id="invoice_date"
                    type="date"
                    showLabelAtTop
                    required
                  />
                  <FormInput
                    register={register}
                    control={control}
                    errors={errors}
                    label="Invoice Number"
                    id="invoice_number"
                    showLabelAtTop
                    required
                  />
                  <FormInput
                    register={register}
                    control={control}
                    errors={errors}
                    label="Due Date"
                    id="due_date"
                    type="date"
                    showLabelAtTop
                  />
                </Flex>
                <Flex gapX={4}>
                  <Box maxWidth="200px">
                    <FormInput
                      maxWidth="200px"
                      register={register}
                      control={control}
                      errors={errors}
                      label="Freight Amount"
                      id="freight_amount"
                      showLabelAtTop
                      type="number"
                    />
                  </Box>

                  <Box maxWidth="200px">
                    <FormInput
                      maxWidth="200px"
                      register={register}
                      control={control}
                      errors={errors}
                      label="Subtotal Amount"
                      id="subtotal_amount"
                      showLabelAtTop
                      required
                      type="number"
                    />
                  </Box>
                  <Box maxWidth="200px">
                    <FormInput
                      maxWidth="200px"
                      register={register}
                      control={control}
                      errors={errors}
                      label="Tax Amount"
                      id="tax_amount"
                      showLabelAtTop
                      type="number"
                    />
                  </Box>
                  <Box maxWidth="200px">
                    <FormInput
                      maxWidth="200px"
                      register={register}
                      control={control}
                      errors={errors}
                      label="Total Amount"
                      id="total_amount"
                      showLabelAtTop
                      required
                      type="number"
                    />
                  </Box>
                </Flex>

                <FormTextArea
                  register={register}
                  control={control}
                  errors={errors}
                  label="Notes"
                  id="notes"
                  showLabelAtTop
                  minH="5lh"
                  maxH="5lh"
                />

                <FormFileUpload
                  control={control}
                  register={register}
                  errors={errors}
                  id="attachmentFile"
                  label="Invoice Attachment file (Max file number: 1)"
                  maxFiles={1}
                  accept={
                    [
                      'application/pdf',
                      'application/msword',
                      'image/*',
                    ] as FileUploadFileMimeType[]
                  }
                  required={isNew}
                />
              </Flex>
            );
          }}
        </FormController>
      </Card.Body>
    </Card.Root>
  );
};

const InvoicesManageCard: React.FC<InvoicesManageCardProps> = ({
  isNew = false,
  po_id,
  invoice_id,
  onClose,
}: InvoicesManageCardProps) => {
  return (
    <Stack>
      <Inner_InvoicesManageCard
        isNew={isNew}
        po_id={po_id}
        invoice_id={invoice_id}
        onClose={onClose}
      />
    </Stack>
  );
};

export default InvoicesManageCard;

interface InvoicesManageCardProps {
  isNew?: boolean;
  onClose?: () => void;
  po_id: number;
  invoice_id?: number; // required for update
}
